// The composed field (slice-2-onboarding.md §1 - no dedicated field/input token exists
// yet, so every input composes existing tokens only: see app.css's .field block for the
// exact composition cited there). Always a real <label>, never placeholder-only
// (input-labels / WCAG 3.3.2) - `hideLabel` (added Slice 3, docs/design/
// slice3-knowledge-ux.md §5.2) keeps the label in the accessibility tree via `.sr-only`
// rather than removing it, for a composer whose visible chrome already names its purpose
// (the "Ask a question about {app}" placeholder/heading context).
// `id`/`inputRef` (Wave-3 fix round, slice3-knowledge-ux.md §5.5): both optional and
// backward-compatible - a caller that omits them gets the exact prior behavior (an
// auto-generated id, no ref). AskCrew.tsx needs both: a stable id its "Skip to
// composer" link can target (§2.9), and a ref so focus can be programmatically
// restored to the composer after an async send/retry (§5.5 "composer clears, keeps
// focus").
import { useId } from "react";

interface FieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly error?: string | null;
  readonly multiline?: boolean;
  readonly rows?: number;
  readonly disabled?: boolean;
  readonly type?: string;
  readonly autoComplete?: string;
  readonly hideLabel?: boolean;
  readonly id?: string;
  readonly inputRef?: React.Ref<HTMLTextAreaElement | HTMLInputElement>;
}

export const Field = ({
  label,
  value,
  onChange,
  placeholder,
  error,
  multiline = false,
  rows = 3,
  disabled = false,
  type = "text",
  autoComplete,
  hideLabel = false,
  id: idProp,
  inputRef,
}: FieldProps): React.JSX.Element => {
  const autoId = useId();
  const id = idProp ?? autoId;
  const errorId = `${id}-error`;

  return (
    <div>
      <label className={hideLabel ? "field-label sr-only" : "field-label"} htmlFor={id}>
        {label}
      </label>
      {multiline ? (
        <textarea
          id={id}
          // The component itself guarantees the invariant this narrows: `multiline`
          // decides which branch renders, so `inputRef` (typed for either element,
          // since a caller does not know ahead of time which one it will get back)
          // is always actually an HTMLTextAreaElement ref here.
          ref={inputRef as React.Ref<HTMLTextAreaElement> | undefined}
          className="field"
          value={value}
          placeholder={placeholder}
          rows={rows}
          disabled={disabled}
          aria-invalid={error != null}
          aria-describedby={error != null ? errorId : undefined}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      ) : (
        <input
          id={id}
          ref={inputRef as React.Ref<HTMLInputElement> | undefined}
          type={type}
          className="field"
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          aria-invalid={error != null}
          aria-describedby={error != null ? errorId : undefined}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      )}
      {error != null ? (
        <p className="field-error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};
